import { useState, useEffect } from 'react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { GitLabProject } from '../types';

interface UseGitLabProjectsResult {
  projects: GitLabProject[];
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook to fetch GitLab projects from the backend API
 */
export const useGitLabProjects = (host?: string): UseGitLabProjectsResult => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const baseUrl = configApi.getString('backend.baseUrl');
        const url = host
          ? `${baseUrl}/api/gitlab-projects?host=${encodeURIComponent(host)}`
          : `${baseUrl}/api/gitlab-projects`;

        // Get authentication token
        const credentials = await identityApi.getCredentials();
        const token = credentials?.token;

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(url, {
          headers,
          credentials: 'include', // Include cookies for authentication
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch GitLab projects: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        setProjects(data);
      } catch (err) {
        setError(err as Error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [configApi, identityApi, host]);

  return {
    projects,
    loading,
    error,
  };
};
